import React, { useEffect, useState } from 'react';
import { Button, Flex, Popconfirm, Tag, Tooltip } from 'antd';
import Table, { MAX_PAGE_SIZE } from 'components/Common/Table';
import { ImportOutlined, UserAddOutlined, EditOutlined, DeleteOutlined, FileTextOutlined, TagsOutlined } from '@ant-design/icons';
import { useNavigate } from "react-router-dom"
import * as actions from "store/actions/AuthActions"
import dayjs from 'dayjs';
import FilterBar from 'components/Common/FilterBar';
import useUrlFilters from 'hooks/useUrlFilters';

const URL_FILTERS = {
  search: { type: 'string' },
  page: { type: 'number', default: 1 },
  pageSize: { type: 'number', key: 'page_size', default: 50 },
};

const ExpensesTable = () => {

  const navigate = useNavigate();
  const [filters, setFilters] = useUrlFilters(URL_FILTERS);
  const urlSetter = (name) => (value) => setFilters({ [name]: value });

  const searchTerm = filters.search || "";
  const setSearchTerm = urlSetter('search');
  const [searchTermLive, setSearchTermLive] = useState(searchTerm);
  useEffect(() => setSearchTermLive(searchTerm), [searchTerm]);

  // Frontend pagination state
  const currentPage = filters.page;
  const setCurrentPage = urlSetter('page');
  const pageSize = Math.min(filters.pageSize, MAX_PAGE_SIZE);
  const setPageSize = urlSetter('pageSize');
  const [totalRows, setTotalRows] = useState(0);

  const [allExpenses, setAllExpenses] = useState([]);
  const [dataSource, setDataSource] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectionType] = useState('checkbox');

  const fetchAllExpenses = async () => {
    setLoading(true);
    try {
      const response = await actions.loadAllExpenses(searchTerm);
      if (response.status === 200) {
        const data = response.data;
        setAllExpenses(data.results || []);
        setTotalRows(data.count || data.results?.length || 0);
      }
    } catch (e) {
      console.error('Error fetching expenses:', e);
    }
    setLoading(false);
  };

  // Frontend pagination logic
  const paginateData = () => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedData = allExpenses.slice(startIndex, endIndex);
    setDataSource(paginatedData);
  };

  const onSearch = (value) => {
    if (searchTerm === value) {
      return;
    } else {
      setSearchTerm(value);
      setCurrentPage(1);
    }
  };


  useEffect(() => {
    fetchAllExpenses();
  }, [searchTerm]);

  // Apply frontend pagination when data or page changes
  useEffect(() => {
    paginateData();
  }, [allExpenses, currentPage, pageSize]);

  const handleDelete = async (id) => {
    const response = await actions.deleteExpenses(id);
    if (response.status === 204) {
      // Refresh the data after deletion
      fetchAllExpenses();
    }
  };

  const rowSelection = {
    onChange: (selectedRowKeys, selectedRows) => {
      console.log(`selectedRowKeys: ${selectedRowKeys}`, 'selectedRows: ', selectedRows);
    },
  }


  // Format currency in INR
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
    }).format(amount || 0);
  };

  const defaultColumns = [
    {
      title: 'DATE',
      dataIndex: 'date',
      key: 'date',
      sorter: (a, b) => new Date(a?.date) - new Date(b?.date),
      width: 120,
      render: (date) => date ? dayjs(date).format('DD/MM/YYYY') : '-',
    },
    {
      title: 'AMOUNT',
      dataIndex: 'amount',
      key: 'amount',
      sorter: (a, b) => (a?.amount || 0) - (b?.amount || 0),
      width: 140,
      align: 'right',
      render: (amount) => (
        <span style={{ fontWeight: 500, color: '#1890ff' }}>
          {formatCurrency(amount)}
        </span>
      ),
    },
    {
      title: 'CATEGORY',
      dataIndex: 'category_name',
      key: 'category_name',
      width: 150,
      render: (name, record) => (
        <Tag color={record.category_color || 'default'}>
          {name || 'Uncategorized'}
        </Tag>
      ),
    },
    {
      title: 'VENDOR',
      dataIndex: 'vendor_name',
      key: 'vendor_name',
      sorter: (a, b) => (a?.vendor_name || '').localeCompare(b?.vendor_name || ''),
      width: 180,
      render: (name) => name || '-',
    },
    {
      title: 'ACCOUNT',
      dataIndex: 'account_name',
      key: 'account_name',
      width: 180,
      render: (name) => name || '-',
    },
    {
      title: 'DESCRIPTION',
      dataIndex: 'public_notes',
      key: 'public_notes',
      width: 200,
      ellipsis: {
        showTitle: false,
      },
      render: (notes) => notes ? (
        <Tooltip placement="topLeft" title={notes}>
          {notes}
        </Tooltip>
      ) : '-',
    },
    {
      title: 'RECEIPT',
      dataIndex: 'has_receipt',
      key: 'has_receipt',
      width: 80,
      align: 'center',
      render: (hasReceipt) => hasReceipt ? (
        <FileTextOutlined style={{ color: '#52c41a' }} />
      ) : (
        <span style={{ color: '#d9d9d9' }}>-</span>
      ),
    },
    {
      title: 'BANK STATEMENT',
      dataIndex: 'bank_transaction_data',
      key: 'bank_transaction',
      width: 150,
      align: 'center',
      sorter: (a, b) => (a?.bank_transaction_data ? 1 : 0) - (b?.bank_transaction_data ? 1 : 0),
      render: (bank_transaction_data) => (
        <span style={{
          padding: '4px 12px',
          borderRadius: '9999px',
          fontSize: '12px',
          fontWeight: 500,
          backgroundColor: bank_transaction_data ? '#52c41a20' : '#8c8c8c20',
          color: bank_transaction_data ? '#52c41a' : '#8c8c8c'
        }}>
          {bank_transaction_data ? 'Attached' : 'Not Attached'}
        </span>
      ),
    },
    {
      title: 'ACTIONS',
      dataIndex: 'edit',
      key: 'edit',
      width: 100,
      fixed: 'right',
      render: (_, record) => (
        <Flex gap="small">
          <Tooltip title="Edit">
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              onClick={() => navigate(`/expenses/edit/${record.id}/`)}
            />
          </Tooltip>
          <Popconfirm
            title="Delete expense?"
            description="This action cannot be undone."
            onConfirm={() => handleDelete(record.id)}
            okText="Delete"
            cancelText="Cancel"
            okButtonProps={{ danger: true }}
          >
            <Tooltip title="Delete">
              <Button
                type="text"
                size="small"
                danger
                icon={<DeleteOutlined />}
              />
            </Tooltip>
          </Popconfirm>
        </Flex>
      ),
    },
  ];

  // const handleAdd = () => {
  //   const newData = {
  //     key: count,
  //     name: `Edward King ${count}`,
  //     age: '32',
  //     address: `London, Park Lane no. ${count}`,
  //   };
  //   setDataSource([...dataSource, newData]);
  //   setCount(count + 1);
  // };

  const handleSave = (row) => {
    const newData = [...dataSource];
    const index = newData.findIndex((item) => row.key === item.key);
    const item = newData[index];
    newData.splice(index, 1, {
      ...item,
      ...row,
    });
    setDataSource(newData);
  };


  const columns = defaultColumns.map((col) => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record) => ({
        record,
        editable: col.editable,
        dataIndex: col.dataIndex,
        title: col.title,
        handleSave,
      }),
    };
  });
  return (
    <div style={{ padding: 10 }}>
      <Flex gap='large' justify='space-between'>
        <Flex gap='large' align='center' wrap>
          <div>
            Showing {((currentPage - 1) * pageSize) + 1}-{Math.min(currentPage * pageSize, totalRows)} of {totalRows} expenses
          </div>

          <FilterBar
            searchValue={searchTermLive}
            onSearchChange={setSearchTermLive}
            onSearch={onSearch}
            searchPlaceholder="Search expenses"
            onRefresh={() => fetchAllExpenses()}
            refreshing={loading}
            addLabel="Add Expense"
            addIcon={<UserAddOutlined />}
            onAdd={() => navigate("/expenses/new")}
          />

        </Flex>
        <Flex gap='large'>
          <Button
            onClick={() => navigate("/expenses/categories")}
            icon={<TagsOutlined />}
            type="default"
          >
            Categories
          </Button>
          <Button
            onClick={() => navigate("/expenses/import")}
            icon={<ImportOutlined />}
            type="default"
          >
            Import
          </Button>
        </Flex>
      </Flex>
      <div style={{ marginTop: 20 }}>
        <Table
          rowSelection={{
            type: selectionType,
            ...rowSelection,
          }}
          loading={loading}
          bordered
          size="small"
          dataSource={dataSource}
          columns={columns}
          rowKey={row => row.id}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: totalRows,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
            pageSizeOptions: ['10', '25', '50', '100'],
            onChange: (page, size) => {
              setCurrentPage(page);
              if (size !== pageSize) {
                setPageSize(size);
                setCurrentPage(1);
              }
            },
          }}
          scroll={{
                x: "max-content", // Horizontal scroll
                y: "calc(100vh - 350px)", // Vertical scroll
              }}
              sticky
        />
      </div>
    </div>
  );
};
export default ExpensesTable;